import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, RefreshCw, ChevronDown,
  X, XCircle, Check, Calendar, Pencil, Clock,
  User, Stethoscope, FileText, ArrowRight, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { CitaFormDialog } from '@/pages/agenda/CitaFormDialog';
import { CobroTab } from '@/pages/consultas/CobroTab';
import { getErrorMessage } from '@/lib/errors';
import {
  type EstadoCita, ESTADO_CITA_INFO, TRANSICIONES_CITA,
  ESTADOS_CITA_TERMINALES, getEstadoCitaInfo, puedeTransicionar,
} from '@/types/cita';

/* ── Types ──────────────────────────────────────────────────────────────────── */

type CanceladoPor = 'paciente' | 'medico' | 'clinica';

/* ── Constants ──────────────────────────────────────────────────────────────── */

const KANBAN_COLS: { id: EstadoCita; label: string; dot: string; ring: string; bg: string }[] = [
  { id: 'programada', label: ESTADO_CITA_INFO.programada.label, dot: ESTADO_CITA_INFO.programada.dotCls, ring: 'ring-amber-300',  bg: 'bg-amber-50/50 border-amber-100' },
  { id: 'confirmada', label: 'Confirmadas',                     dot: ESTADO_CITA_INFO.confirmada.dotCls, ring: 'ring-blue-300',   bg: 'bg-blue-50/50 border-blue-100' },
  { id: 'espera',     label: ESTADO_CITA_INFO.espera.label,     dot: ESTADO_CITA_INFO.espera.dotCls,     ring: 'ring-violet-300', bg: 'bg-violet-50/50 border-violet-100' },
  { id: 'en_curso',   label: ESTADO_CITA_INFO.en_curso.label,   dot: ESTADO_CITA_INFO.en_curso.dotCls,   ring: 'ring-green-300',  bg: 'bg-green-50/50 border-green-100' },
];

// Drop targets válidos por drag & drop en el Kanban — subconjunto del grafo
// completo (TRANSICIONES_CITA) que excluye los estados terminales, a los que
// solo se llega por acción explícita (Cancelar / Completar / No asistió).
const TRANSITIONS: Record<EstadoCita, EstadoCita[]> = Object.fromEntries(
  Object.entries(TRANSICIONES_CITA).map(([estado, destinos]) => [
    estado,
    destinos.filter(d => !ESTADOS_CITA_TERMINALES.includes(d)),
  ]),
) as Record<EstadoCita, EstadoCita[]>;

/* ── Utilities ──────────────────────────────────────────────────────────────── */

function toYMD(d: Date) { return d.toISOString().slice(0, 10); }
function addDays(d: Date, n: number) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function fmtHora(iso: string) {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}
function fmtFecha(d: Date) {
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
}
function nomPaciente(c: any) {
  return c.paciente ? `${c.paciente.nombre} ${c.paciente.apellidoPaterno || ''}`.trim() : 'Paciente';
}

/* ── Cancel Modal ────────────────────────────────────────────────────────────── */

function CancelModal({
  cita, onClose, onConfirm, isPending,
}: {
  cita: any;
  onClose: () => void;
  onConfirm: (motivo: string, por: CanceladoPor) => void;
  isPending: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  const [por,    setPor]    = useState<CanceladoPor>('paciente');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-semibold text-sm text-text-primary">Cancelar cita</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-text-secondary">
            ¿Cancelar la cita de{' '}
            <span className="font-semibold text-text-primary">{nomPaciente(cita)}</span>
            {' '}a las {fmtHora(cita.fechaInicio)}?
          </p>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">¿Quién cancela? *</label>
            <div className="flex gap-2">
              {(['paciente', 'medico', 'clinica'] as CanceladoPor[]).map(p => (
                <button key={p} type="button" onClick={() => setPor(p)}
                  className={cn(
                    'flex-1 py-2 rounded-xl border text-xs font-semibold capitalize transition-colors',
                    por === p
                      ? 'border-red-400 bg-red-50 text-red-600'
                      : 'border-gray-200 text-text-secondary hover:bg-gray-50',
                  )}>
                  {p === 'medico' ? 'Médico' : p === 'clinica' ? 'Clínica' : 'Paciente'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Motivo (opcional)</label>
            <textarea rows={3} value={motivo} onChange={e => setMotivo(e.target.value)}
              placeholder="Describe brevemente el motivo..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="text-sm border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 text-text-secondary transition-colors">
            Volver
          </button>
          <button disabled={isPending} onClick={() => onConfirm(motivo, por)}
            className="flex items-center gap-1.5 text-sm bg-red-500 text-white px-5 py-2 rounded-lg hover:bg-red-600 disabled:opacity-60 transition-colors font-medium">
            <XCircle size={14} />
            {isPending ? 'Cancelando...' : 'Confirmar cancelación'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Kanban Card ──────────────────────────────────────────────────────────────── */

function CitaCard({
  cita, onDragStart, onClick, onCancel, onCompletar,
}: {
  cita: any;
  onDragStart: () => void;
  onClick: () => void;
  onCancel: () => void;
  onCompletar: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; onDragStart(); }}
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-100 shadow-sm p-3.5 cursor-grab active:cursor-grabbing hover:shadow-md hover:border-gray-200 transition-all select-none group"
    >
      {/* Time + cancel */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-text-secondary font-mono tabular-nums">
          {fmtHora(cita.fechaInicio)} – {fmtHora(cita.fechaFin)}
        </span>
        <button
          onClick={e => { e.stopPropagation(); onCancel(); }}
          className="p-0.5 rounded-full text-gray-300 hover:text-red-400 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
          title="Cancelar">
          <XCircle size={13} />
        </button>
      </div>

      {/* Patient */}
      <p className="text-sm font-semibold text-text-primary leading-tight">{nomPaciente(cita)}</p>

      {/* Doctor */}
      <p className="text-xs text-text-secondary mt-0.5 mb-2">{cita.medico?.nombre ?? '—'}</p>

      {/* CIE-11 */}
      {cita.tituloCie11 && (
        <p className="text-[10.5px] text-primary-600 bg-primary-50 rounded-md px-2 py-0.5 mb-2 line-clamp-1">
          {cita.codigoCie11 && (
            <span className="font-mono font-bold mr-1">{cita.codigoCie11}</span>
          )}
          {cita.tituloCie11}
        </p>
      )}

      {/* Tipo + Completar */}
      <div className="flex items-center justify-between mt-1">
        <span className={cn(
          'text-[10px] px-1.5 py-0.5 rounded-full border font-medium',
          cita.tipo === 'teleconsulta'
            ? 'bg-purple-50 text-purple-600 border-purple-200'
            : 'bg-gray-50 text-gray-500 border-gray-200',
        )}>
          {cita.tipo === 'teleconsulta' ? 'Teleconsulta' : 'Presencial'}
        </span>

        {cita.estado === 'en_curso' && (
          <button
            onClick={e => { e.stopPropagation(); onCompletar(); }}
            className="flex items-center gap-1 text-[10.5px] font-semibold text-green-600 bg-green-50 hover:bg-green-100 px-2 py-1 rounded-lg border border-green-200 transition-colors">
            <Check size={10} /> Completar
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Kanban Column ────────────────────────────────────────────────────────────── */

function KanbanCol({
  col, citas, draggedId,
  onDragStart, onDrop, onClick, onCancel, onCompletar,
}: {
  col: typeof KANBAN_COLS[0];
  citas: any[];
  draggedId: React.MutableRefObject<string | null>;
  onDragStart: (id: string) => void;
  onDrop: (target: EstadoCita) => void;
  onClick: (c: any) => void;
  onCancel: (c: any) => void;
  onCompletar: (c: any) => void;
}) {
  const [collapsed,  setCollapsed]  = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  /* ── Collapsed: thin vertical strip ──────────────────────────────────── */
  if (collapsed) {
    return (
      <div
        className={cn(
          'flex flex-col items-center rounded-2xl border w-10 flex-shrink-0 cursor-pointer select-none transition-all',
          col.bg,
          isDragOver && `ring-2 ${col.ring} ring-offset-1`,
        )}
        onClick={() => setCollapsed(false)}
        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={e => { e.preventDefault(); setIsDragOver(false); onDrop(col.id); }}
        title={`${col.label} (${citas.length})`}
      >
        <div className="py-3 flex flex-col items-center gap-2">
          <span className={cn('w-2 h-2 rounded-full flex-shrink-0', col.dot)} />
          <span className="text-[10px] bg-white/80 text-text-secondary px-1 py-0.5 rounded-full border border-black/5 font-medium">
            {citas.length}
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center py-4">
          <span
            className="text-[10px] font-semibold text-text-secondary whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {col.label}
          </span>
        </div>
        <div className="pb-3">
          <ChevronDown size={12} className="text-text-secondary rotate-[-90deg]" />
        </div>
      </div>
    );
  }

  /* ── Expanded ──────────────────────────────────────────────────────────── */
  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl border min-w-[220px] flex-1 max-w-xs transition-all',
        col.bg,
        isDragOver && `ring-2 ${col.ring} ring-offset-1`,
      )}
      onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={e => { e.preventDefault(); setIsDragOver(false); onDrop(col.id); }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-3 border-b border-black/5">
        <div className="flex items-center gap-2">
          <span className={cn('w-2 h-2 rounded-full flex-shrink-0', col.dot)} />
          <span className="text-xs font-semibold text-text-primary">{col.label}</span>
          <span className="text-[10px] bg-white/80 text-text-secondary px-1.5 py-0.5 rounded-full border border-black/5 font-medium">
            {citas.length}
          </span>
        </div>
        <button onClick={() => setCollapsed(true)}
          className="p-0.5 rounded hover:bg-white/60 text-text-secondary transition-colors">
          <ChevronDown size={14} />
        </button>
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-2.5 space-y-2 min-h-[72px]">
        {citas.length === 0 && (
          <p className="text-center py-6 text-[11px] text-text-secondary/50">Sin citas</p>
        )}
        {citas.map(c => (
          <CitaCard
            key={c.id}
            cita={c}
            onDragStart={() => onDragStart(c.id)}
            onClick={() => onClick(c)}
            onCancel={() => onCancel(c)}
              onCompletar={() => onCompletar(c)}
            />
          ))}
        </div>
    </div>
  );
}

/* ── Cita Detail Panel ────────────────────────────────────────────────────────── */

const NEXT_ESTADO: Partial<Record<EstadoCita, { estado: EstadoCita; label: string; cls: string }>> = {
  programada: { estado: 'confirmada', label: 'Confirmar cita',         cls: 'bg-blue-500 hover:bg-blue-600 text-white' },
  confirmada: { estado: 'espera',     label: 'Mover a sala de espera', cls: 'bg-violet-500 hover:bg-violet-600 text-white' },
};

function DetailRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="mt-0.5 flex-shrink-0 w-4 text-text-secondary/60">
        <Icon size={14} />
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary/60 mb-0.5">{label}</p>
        <div className="text-sm text-text-primary">{value}</div>
      </div>
    </div>
  );
}

function CitaDetailPanel({
  cita, onClose, onEdit, onCancel, onNoAsistio, onTransicion, isTransicionPending, onCobro,
}: {
  cita: any;
  onClose: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onNoAsistio: () => void;
  onTransicion: (estado: EstadoCita) => void;
  isTransicionPending: boolean;
  onCobro: () => void;
}) {
  const navigate = useNavigate();
  const ei   = getEstadoCitaInfo(cita.estado);
  const next = NEXT_ESTADO[cita.estado as EstadoCita];
  const isTerminal = ESTADOS_CITA_TERMINALES.includes(cita.estado);
  const puedeNoAsistio = puedeTransicionar(cita.estado, 'no_asistio');
  const puedeConsulta = !['programada', 'cancelada'].includes(cita.estado);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <div className="flex-1 min-w-0 pr-3">
            <p className="font-bold text-base text-text-primary leading-tight">{nomPaciente(cita)}</p>
            <span className={cn('inline-block mt-1.5 text-[10.5px] px-2 py-0.5 rounded-full border font-semibold', ei.badgeCls)}>
              {ei.label}
            </span>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 flex-shrink-0 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          <DetailRow icon={Clock} label="Horario"
            value={`${fmtHora(cita.fechaInicio)} – ${fmtHora(cita.fechaFin)}`} />

          <DetailRow icon={User} label="Doctor"
            value={cita.medico?.nombre ?? '—'} />

          <DetailRow icon={Stethoscope} label="Tipo"
            value={cita.tipo === 'teleconsulta' ? 'Teleconsulta' : 'Presencial'} />

          {cita.tituloCie11 && (
            <DetailRow icon={FileText} label="Diagnóstico CIE-11"
              value={
                <span>
                  {cita.codigoCie11 && (
                    <span className="font-mono font-bold text-primary-600 mr-1.5">{cita.codigoCie11}</span>
                  )}
                  {cita.tituloCie11}
                </span>
              } />
          )}

          {cita.motivo && (
            <DetailRow icon={FileText} label="Motivo de consulta"
              value={<span className="text-text-secondary">{cita.motivo}</span>} />
          )}

          {cita.notas && (
            <DetailRow icon={FileText} label="Notas"
              value={<span className="text-text-secondary whitespace-pre-wrap">{cita.notas}</span>} />
          )}

          {cita.estado === 'cancelada' && (
            <div className="bg-red-50 rounded-xl p-3.5 space-y-1.5 border border-red-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-400">Cancelación</p>
              {cita.canceladoPor && (
                <p className="text-xs text-red-700">
                  Cancelado por: <span className="font-semibold capitalize">{cita.canceladoPor}</span>
                </p>
              )}
              {cita.motivoCancelacion && (
                <p className="text-xs text-red-600">{cita.motivoCancelacion}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-gray-100 space-y-2">
          {/* Main transition button */}
          {next && (
            <button
              disabled={isTransicionPending}
              onClick={() => onTransicion(next.estado)}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60',
                next.cls,
              )}>
              <ArrowRight size={14} />
              {next.label}
            </button>
          )}

          {/* Abrir consulta */}
          {puedeConsulta && (
            <button
              onClick={() => { onClose(); navigate(`/consultas/${cita.id}`); }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-colors">
              <Stethoscope size={14} /> Abrir consulta
            </button>
          )}

          {/* Cobro */}
          <button
            onClick={onCobro}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-primary-200 text-primary-600 hover:bg-primary-50 text-sm font-semibold transition-colors">
            <Wallet size={14} /> Cobro
          </button>

          {/* Edit + Cancel row */}
          <div className="flex gap-2">
            {!isTerminal && (
              <button onClick={onEdit}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-gray-200 text-sm font-medium text-text-secondary hover:bg-gray-50 transition-colors">
                <Pencil size={13} /> Editar
              </button>
            )}
            {!isTerminal && (
              <button onClick={onCancel}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-red-200 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors">
                <XCircle size={13} /> Cancelar
              </button>
            )}
          </div>

          {/* No asistió — solo mientras la cita aún no arranca (programada/confirmada/espera) */}
          {puedeNoAsistio && (
            <button onClick={onNoAsistio} disabled={isTransicionPending}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-orange-200 text-sm font-medium text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-60">
              <Clock size={13} /> Marcar como no asistió
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Schedule Table ───────────────────────────────────────────────────────────── */

function AgendaTable({ citas, onEdit }: { citas: any[]; onEdit: (c: any) => void }) {
  const sorted = [...citas].sort((a, b) =>
    new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime()
  );

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-text-secondary border-b border-gray-100 bg-gray-50/60">
            <th className="text-left px-4 py-3 font-medium">Hora</th>
            <th className="text-left px-4 py-3 font-medium">Paciente</th>
            <th className="text-left px-4 py-3 font-medium">Diagnóstico</th>
            <th className="text-left px-4 py-3 font-medium">Doctor</th>
            <th className="text-left px-4 py-3 font-medium">Estado</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sorted.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center py-10 text-sm text-text-secondary">
                Sin citas para este día
              </td>
            </tr>
          )}
          {sorted.map(c => {
            const ei = getEstadoCitaInfo(c.estado);
            return (
              <tr key={c.id} onClick={() => onEdit(c)}
                className="hover:bg-gray-50/60 cursor-pointer transition-colors">
                <td className="px-4 py-3 font-mono text-xs font-semibold text-text-secondary whitespace-nowrap">
                  {fmtHora(c.fechaInicio)}
                </td>
                <td className="px-4 py-3 font-medium text-text-primary whitespace-nowrap">
                  {nomPaciente(c)}
                </td>
                <td className="px-4 py-3 max-w-[200px]">
                  {c.tituloCie11 ? (
                    <span className="text-xs text-text-secondary">
                      {c.codigoCie11 && (
                        <span className="font-mono font-bold text-primary-600 mr-1">{c.codigoCie11}</span>
                      )}
                      <span className="line-clamp-1">{c.tituloCie11}</span>
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-text-secondary whitespace-nowrap">
                  {c.medico?.nombre ?? '—'}
                </td>
                <td className="px-4 py-3">
                  <span className={cn('text-[10.5px] px-2 py-0.5 rounded-full border font-semibold', ei.badgeCls)}>
                    {ei.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Main Page ────────────────────────────────────────────────────────────────── */

export function RecepcionPage() {
  const qc = useQueryClient();
  const [fecha,        setFecha]        = useState(new Date());
  const [filtroMedico, setFiltroMedico] = useState('');
  const [viendo,       setViendo]       = useState<any | null>(null);
  const [cancelando,   setCancelando]   = useState<any | null>(null);
  const [editando,     setEditando]     = useState<any | null>(null);
  const [cobrando,     setCobrando]     = useState<any | null>(null);
  const draggedId = useRef<string | null>(null);

  const ymd   = toYMD(fecha);
  const esHoy = ymd === toYMD(new Date());

  /* Queries */
  const { data: citas = [], isLoading, refetch } = useQuery<any[]>({
    queryKey: ['citas-recepcion', ymd],
    queryFn: () => api.get('/api/citas', {
      params: { fechaInicio: `${ymd}T00:00:00`, fechaFin: `${ymd}T23:59:59` },
    }).then(r => r.data),
    refetchInterval: 60_000,
  });

  const { data: medicos   = [] } = useQuery({ queryKey: ['medicos'],           queryFn: () => api.get('/api/medicos').then(r => r.data) });
  const { data: pacientes = [] } = useQuery({ queryKey: ['pacientes-select'],  queryFn: () => api.get('/api/pacientes').then(r => r.data) });
  const { data: clinicas  = [] } = useQuery({ queryKey: ['clinicas'],          queryFn: () => api.get('/api/clinicas').then(r => r.data) });

  /* Mutations */
  const estadoMutation = useMutation({
    mutationFn: ({ id, ...patch }: any) => api.patch(`/api/citas/${id}`, patch).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['citas-recepcion', ymd] }),
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar la cita')),
  });

  const submitMutation = useMutation({
    mutationFn: (dto: any) => dto.id
      ? api.patch(`/api/citas/${dto.id}`, dto).then(r => r.data)
      : api.post('/api/citas', dto).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citas-recepcion', ymd] });
      setEditando(null);
      setViendo(null);
      toast.success('Cita actualizada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  /* Filtered citas */
  const filtradas: any[] = useMemo(() =>
    filtroMedico
      ? citas.filter(c => c.medicoId === filtroMedico)
      : citas,
    [citas, filtroMedico]
  );

  /* KPIs */
  const kpis = useMemo(() => ({
    total:      filtradas.length,
    enEspera:   filtradas.filter(c => c.estado === 'espera').length,
    enConsulta: filtradas.filter(c => c.estado === 'en_curso').length,
    completadas: filtradas.filter(c => c.estado === 'completada').length,
  }), [filtradas]);

  /* Kanban groups: only active (non-terminal) estados */
  const kanbanGroups = useMemo(() => {
    const active = filtradas.filter(c => !ESTADOS_CITA_TERMINALES.includes(c.estado));
    return Object.fromEntries(
      KANBAN_COLS.map(col => [
        col.id,
        active
          .filter(c => c.estado === col.id)
          .sort((a, b) => new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime()),
      ])
    ) as Record<string, any[]>;
  }, [filtradas]);

  /* Drag & drop */
  function handleDrop(target: EstadoCita) {
    const id = draggedId.current;
    if (!id) return;
    const cita = citas.find(c => c.id === id);
    if (!cita || cita.estado === target) return;
    const allowed = TRANSITIONS[cita.estado as EstadoCita] ?? [];
    if (!allowed.includes(target)) {
      toast.error(`No se puede mover de "${getEstadoCitaInfo(cita.estado).label}" a "${getEstadoCitaInfo(target).label}"`);
      draggedId.current = null;
      return;
    }
    estadoMutation.mutate({ id, estado: target });
    draggedId.current = null;
  }

  function handleCancel(motivo: string, canceladoPor: CanceladoPor) {
    if (!cancelando) return;
    estadoMutation.mutate(
      { id: cancelando.id, estado: 'cancelada', motivoCancelacion: motivo || undefined, canceladoPor },
      { onSuccess: () => { setCancelando(null); toast.success('Cita cancelada'); } },
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Recepción</h2>
          <p className="text-sm text-text-secondary mt-0.5 capitalize">{fmtFecha(fecha)}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()}
            className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
            title="Actualizar">
            <RefreshCw size={15} />
          </button>
          <button onClick={() => setFecha(p => addDays(p, -1))}
            className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
            <ChevronLeft size={16} />
          </button>
          {!esHoy && (
            <button onClick={() => setFecha(new Date())}
              className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors">
              Hoy
            </button>
          )}
          <button onClick={() => setFecha(p => addDays(p, 1))}
            className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total del día',      value: kpis.total,       cls: 'text-blue-600',   bg: 'bg-blue-50/60' },
          { label: 'En sala de espera',  value: kpis.enEspera,    cls: 'text-violet-600', bg: 'bg-violet-50/60' },
          { label: 'En consulta',        value: kpis.enConsulta,  cls: 'text-green-600',  bg: 'bg-green-50/60' },
          { label: 'Completadas',        value: kpis.completadas, cls: 'text-gray-500',   bg: 'bg-gray-50' },
        ].map(k => (
          <div key={k.label} className={cn('rounded-xl border border-gray-100 p-4', k.bg)}>
            <p className={cn('text-2xl font-bold', k.cls)}>{k.value}</p>
            <p className="text-xs text-text-secondary mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      {/* ── Filter ── */}
      <div className="flex items-center gap-3">
        <select value={filtroMedico} onChange={e => setFiltroMedico(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
          <option value="">Todos los médicos</option>
          {(medicos as any[]).map((m: any) => (
            <option key={m.id} value={m.id}>{m.nombre}</option>
          ))}
        </select>
        <span className="text-sm text-text-secondary">
          {filtradas.length} cita{filtradas.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Kanban Board ── */}
      {isLoading ? (
        <div className="flex gap-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex-1 min-w-[220px] h-48 bg-gray-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {KANBAN_COLS.map(col => (
            <KanbanCol
              key={col.id}
              col={col}
              citas={kanbanGroups[col.id] ?? []}
              draggedId={draggedId}
              onDragStart={id => { draggedId.current = id; }}
              onDrop={handleDrop}
              onClick={setViendo}
              onCancel={c => { setViendo(null); setCancelando(c); }}
              onCompletar={c => estadoMutation.mutate({ id: c.id, estado: 'completada' })}
            />
          ))}
        </div>
      )}

      {/* ── Agenda Table ── */}
      <div className="space-y-2.5">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Calendar size={14} className="text-text-secondary" />
          Agenda del día
        </h3>
        <AgendaTable citas={filtradas} onEdit={setViendo} />
      </div>

      {/* ── Detail Panel ── */}
      {viendo && !editando && !cancelando && !cobrando && (
        <CitaDetailPanel
          cita={viendo}
          onClose={() => setViendo(null)}
          onEdit={() => setEditando(viendo)}
          onCancel={() => { setCancelando(viendo); setViendo(null); }}
          onCobro={() => { setCobrando(viendo); setViendo(null); }}
          onNoAsistio={() => estadoMutation.mutate(
            { id: viendo.id, estado: 'no_asistio' },
            { onSuccess: () => { setViendo(null); toast.success('Cita marcada como no asistió'); } },
          )}
          onTransicion={estado => estadoMutation.mutate(
            { id: viendo.id, estado },
            { onSuccess: () => setViendo(null) },
          )}
          isTransicionPending={estadoMutation.isPending}
        />
      )}

      {/* ── Cobro Panel ── */}
      {cobrando && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <p className="font-bold text-sm text-text-primary">{nomPaciente(cobrando)}</p>
                <p className="text-xs text-text-secondary mt-0.5">Cobro de consulta</p>
              </div>
              <button onClick={() => setCobrando(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <CobroTab
                citaId={cobrando.id}
                pacienteId={cobrando.pacienteId}
                clinicaId={cobrando.clinicaId}
                canEdit
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel Modal ── */}
      {cancelando && (
        <CancelModal
          cita={cancelando}
          onClose={() => setCancelando(null)}
          onConfirm={handleCancel}
          isPending={estadoMutation.isPending}
        />
      )}

      {/* ── Edit Dialog ── */}
      <CitaFormDialog
        open={!!editando}
        onOpenChange={open => { if (!open) setEditando(null); }}
        pacientes={pacientes as any[]}
        medicos={medicos as any[]}
        clinicas={clinicas as any[]}
        editingCita={editando ?? undefined}
        onSubmit={dto => submitMutation.mutate(editando ? { ...dto, id: editando.id } : dto)}
        isSubmitting={submitMutation.isPending}
      />

    </div>
  );
}
